import React, { useEffect, useMemo, useRef, useState } from "react";
import DOMPurify from "dompurify";
import { CommentType } from "../types";

type CommentNode = Omit<CommentType, "parentId" | "replies"> & {
  parentId: number | null;
  replies: CommentNode[];
};

interface CommentNavigatorProps {
  comments: CommentType[];
  editId?: number;
  currentUserId?: number;
  currentUserRole?: string;
  onReply?: (commentId: number, content: string) => Promise<void>;
  onSoftRemove?: (commentId: number) => Promise<void>;
  onHardDeleteThread?: (commentId: number) => Promise<void>;
}

const dateFormatter = new Intl.DateTimeFormat(undefined, {
  dateStyle: "medium",
});

const stripHtml = (html: string): string => {
  const cleaned = DOMPurify.sanitize(html || "", { ALLOWED_TAGS: [] });
  return cleaned.replace(/\s+/g, " ").trim();
};

const isModeratorRole = (role?: string): boolean => {
  return role === "admin" || role === "moderator";
};

const getTimestamp = (dateStr?: string | null): number => {
  if (!dateStr) return 0;
  const parsed = Date.parse(dateStr);
  return Number.isNaN(parsed) ? 0 : parsed;
};

const formatRelativeTime = (dateStr?: string | null): string => {
  if (!dateStr) return "unknown time";
  const parsed = Date.parse(dateStr);
  if (Number.isNaN(parsed)) return "unknown time";

  const diff = Date.now() - parsed;
  const minute = 60 * 1000;
  const hour = 60 * minute;
  const day = 24 * hour;

  if (diff < minute) return "just now";
  if (diff < hour) return `${Math.max(1, Math.floor(diff / minute))} min ago`;
  if (diff < day) return `${Math.max(1, Math.floor(diff / hour))} hours ago`;
  if (diff < 2 * day) return "yesterday";
  if (diff < 30 * day) return `${Math.floor(diff / day)} days ago`;

  return dateFormatter.format(parsed);
};

const collectComments = (comments: CommentType[]): CommentType[] => {
  const map = new Map<number, CommentType>();

  const walk = (items: CommentType[] = []) => {
    items.forEach((comment) => {
      if (!comment || typeof comment.id !== "number") return;
      map.set(comment.id, comment);
      if (comment.replies?.length) walk(comment.replies);
    });
  };

  walk(comments);
  return Array.from(map.values());
};

const buildCommentTree = (comments: CommentType[]): CommentNode[] => {
  const allComments = collectComments(comments);
  const map = new Map<number, CommentNode>();

  allComments.forEach((comment) => {
    map.set(comment.id, {
      ...comment,
      parentId: comment.parentId ?? null,
      replies: [],
    });
  });

  const roots: CommentNode[] = [];

  allComments.forEach((comment) => {
    const node = map.get(comment.id);
    if (!node) return;

    const parentId = comment.parentId ?? null;
    const parent = parentId === null ? null : map.get(parentId);

    if (!parent) roots.push(node);
    else parent.replies.push(node);
  });

  const sortRecursive = (nodes: CommentNode[]) => {
    nodes.sort((a, b) => getTimestamp(b.createdAt) - getTimestamp(a.createdAt));
    nodes.forEach((node) => sortRecursive(node.replies));
  };

  sortRecursive(roots);
  return roots;
};

const flattenAll = (nodes: CommentNode[]): CommentNode[] => {
  const result: CommentNode[] = [];

  const walk = (items: CommentNode[]) => {
    items.forEach((item) => {
      result.push(item);
      walk(item.replies);
    });
  };

  walk(nodes);
  return result;
};

const countAllReplies = (node: CommentNode): number => {
  return node.replies.reduce((count, reply) => count + 1 + countAllReplies(reply), 0);
};

const getParentPath = (allById: Map<number, CommentNode>, commentId: number): number[] => {
  const path: number[] = [];
  let current: CommentNode | undefined = allById.get(commentId);

  while (current) {
    path.unshift(current.id);
    current = current.parentId === null ? undefined : allById.get(current.parentId);
  }

  return path;
};

const findRootForComment = (allById: Map<number, CommentNode>, comment: CommentNode): CommentNode => {
  let current = comment;

  while (current.parentId !== null) {
    const parent = allById.get(current.parentId);
    if (!parent) break;
    current = parent;
  }

  return current;
};

const getColorClass = (depth: number): string => {
  if (depth === 1) return "is-amber";
  if (depth === 2) return "is-olive";
  if (depth === 3) return "is-plum";
  return "is-brown";
};

const CommentNavigator: React.FC<CommentNavigatorProps> = ({
  comments,
  editId,
  currentUserId,
  currentUserRole,
  onReply,
  onSoftRemove,
  onHardDeleteThread,
}) => {
  const roots = useMemo(() => buildCommentTree(comments), [comments]);
  const allComments = useMemo(() => flattenAll(roots), [roots]);
  const allById = useMemo(() => new Map(allComments.map((comment) => [comment.id, comment])), [allComments]);
  const [selectedRootId, setSelectedRootId] = useState<number | null>(null);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [openReplies, setOpenReplies] = useState<Set<number>>(new Set());
  const previousRootIdsRef = useRef<Set<number>>(new Set());
  const selectedRef = useRef<HTMLElement | null>(null);
  const canModerate = isModeratorRole(currentUserRole);

  const selectedRoot = selectedRootId === null ? null : roots.find((root) => root.id === selectedRootId) ?? null;
  const selectedRootIndex = selectedRoot ? roots.findIndex((root) => root.id === selectedRoot.id) : -1;

  const getEditContextLabel = (comment: CommentType | CommentNode): string | null => {
    if (!comment.editId) return "gist-wide";
    if (!editId) return `edit ${comment.editId}`;
    return comment.editId === editId ? "current edit" : `older edit ${comment.editId}`;
  };

  useEffect(() => {
    const currentRootIds = new Set(roots.map((root) => root.id));
    const previousRootIds = previousRootIdsRef.current;
    const newRoots = roots.filter((root) => !previousRootIds.has(root.id));

    if (roots.length === 0) {
      setSelectedRootId(null);
      setSelectedId(null);
      setOpenReplies(new Set());
      previousRootIdsRef.current = currentRootIds;
      return;
    }

    setSelectedRootId((previousSelectedRootId) => {
      if (previousRootIds.size > 0 && newRoots.length > 0) {
        return newRoots[0].id;
      }

      if (previousSelectedRootId && currentRootIds.has(previousSelectedRootId)) {
        return previousSelectedRootId;
      }

      return roots[0].id;
    });

    setSelectedId((previousSelectedId) => {
      if (previousRootIds.size > 0 && newRoots.length > 0) {
        return newRoots[0].id;
      }

      if (previousSelectedId && allById.has(previousSelectedId)) {
        return previousSelectedId;
      }

      return roots[0].id;
    });

    setOpenReplies((previousOpenReplies) => {
      const next = new Set<number>();
      previousOpenReplies.forEach((id) => {
        if (allById.has(id)) next.add(id);
      });
      return next;
    });

    previousRootIdsRef.current = currentRootIds;
  }, [roots, allById]);

  const scrollSelectedIntoView = () => {
    window.setTimeout(() => {
      selectedRef.current?.scrollIntoView({ block: "center", behavior: "smooth" });
    }, 40);
  };

  const selectRootByIndex = (index: number) => {
    if (roots.length === 0) return;
    const normalizedIndex = ((index % roots.length) + roots.length) % roots.length;
    const root = roots[normalizedIndex];

    setSelectedRootId(root.id);
    setSelectedId(root.id);
    window.setTimeout(() => {
      selectedRef.current?.scrollIntoView({ block: "nearest", behavior: "smooth" });
    }, 40);
  };

  const selectComment = (comment: CommentNode, shouldOpenAncestors = false) => {
    const root = findRootForComment(allById, comment);

    setSelectedRootId(root.id);
    setSelectedId(comment.id);

    if (shouldOpenAncestors) {
      const path = getParentPath(allById, comment.id);
      setOpenReplies((prev) => {
        const next = new Set(prev);
        path.slice(0, -1).forEach((id) => next.add(id));
        return next;
      });
    }

    scrollSelectedIntoView();
  };

  const toggleReplies = (comment: CommentNode) => {
    setOpenReplies((prev) => {
      const next = new Set(prev);
      if (next.has(comment.id)) next.delete(comment.id);
      else next.add(comment.id);
      return next;
    });

    selectComment(comment);
  };

  const collapseCurrentBranch = () => {
    if (!selectedRoot) return;

    const idsToClose = new Set<number>();
    const collect = (node: CommentNode) => {
      idsToClose.add(node.id);
      node.replies.forEach(collect);
    };

    collect(selectedRoot);

    setOpenReplies((prev) => {
      const next = new Set(prev);
      idsToClose.forEach((id) => next.delete(id));
      return next;
    });

    setSelectedId(selectedRoot.id);
    scrollSelectedIntoView();
  };

  const handleReply = async (comment: CommentNode) => {
    if (!onReply) return;
    const content = window.prompt("Write your reply:");
    if (!content?.trim()) return;

    await onReply(comment.id, content.trim());

    const root = findRootForComment(allById, comment);
    setSelectedRootId(root.id);
    setSelectedId(comment.id);
    setOpenReplies((prev) => {
      const next = new Set(prev);
      next.add(comment.id);
      getParentPath(allById, comment.id).slice(0, -1).forEach((id) => next.add(id));
      return next;
    });
  };

  const handleSoftRemove = async (comment: CommentNode) => {
    if (!onSoftRemove) return;

    const isOwner = currentUserId === comment.user?.id || currentUserId === comment.userId;
    const promptText = canModerate && !isOwner
      ? "Soft remove this comment? The comment text will become a moderator placeholder, but all child replies will stay visible."
      : "Remove your comment? It will become a technical 'Comment removed' placeholder, but child replies will stay visible.";

    if (!window.confirm(promptText)) return;

    await onSoftRemove(comment.id);
    selectComment(comment);
  };

  const handleHardDeleteThread = async (comment: CommentNode) => {
    if (!onHardDeleteThread || !canModerate) return;

    const childCount = countAllReplies(comment);
    const promptText = childCount > 0
      ? `Permanently delete this comment and its ${childCount} child ${childCount === 1 ? "reply" : "replies"} from the database? This cannot be undone.`
      : "Permanently delete this comment from the database? This cannot be undone.";

    if (!window.confirm(promptText)) return;

    const root = findRootForComment(allById, comment);
    const nextRoot = root.id === comment.id
      ? roots.find((candidate) => candidate.id !== root.id) ?? null
      : root;

    await onHardDeleteThread(comment.id);

    setSelectedRootId(nextRoot?.id ?? null);
    setSelectedId(nextRoot?.id ?? null);
  };

  if (comments.length === 0 || roots.length === 0) {
    return (
      <section className="comment-nav-shell comment-book-shell">
        <div className="comment-nav-empty">No comments yet. Be the first to comment!</div>
      </section>
    );
  }

  const largestBranch = roots.reduce((max, comment) => Math.max(max, countAllReplies(comment)), 0);
  const currentRootTotalReplies = selectedRoot ? countAllReplies(selectedRoot) : 0;

  const renderCommentCard = (comment: CommentNode, depth: number): React.ReactNode => {
    const isRoot = depth === 1;
    const isSelected = selectedId === comment.id;
    const isRemoved = comment.isRemoved === true;
    const isOwner = currentUserId === comment.user?.id || currentUserId === comment.userId;
    const hasReplies = comment.replies.length > 0;
    const isOpen = openReplies.has(comment.id);
    const totalReplies = countAllReplies(comment);
    const canSoftRemove = !isRemoved && Boolean(onSoftRemove) && Boolean(currentUserId) && (isOwner || canModerate);
    const canHardDelete = Boolean(onHardDeleteThread) && canModerate;
    const text = stripHtml(comment.comment);

    return (
      <article
        key={comment.id}
        ref={isSelected ? selectedRef : undefined}
        className={`comment-book-card ${getColorClass(depth)}${isRoot ? " is-root" : " is-child"}${
          isSelected ? " is-selected" : ""
        }${isRemoved ? " is-removed" : ""}`}
        onClick={() => selectComment(comment)}
      >
        <div className="comment-book-card-head">
          <div className="comment-nav-author">
            <span className="comment-nav-avatar">{(comment.user?.name || "?").slice(0, 1).toUpperCase()}</span>
            <span>
              <span className="comment-nav-author-name">{comment.user?.name || "Anonymous"}</span>
              <span className="comment-nav-time">
                {formatRelativeTime(comment.createdAt)} · {isRoot ? "top-level reply" : `level ${depth}`}
              </span>
            </span>
          </div>
          <div className="comment-nav-status-badges">
            {isRemoved && <span className="comment-nav-badge is-removed">removed</span>}
            {getEditContextLabel(comment) && (
              <span className="comment-nav-badge is-context">{getEditContextLabel(comment)}</span>
            )}
            {hasReplies ? (
              <span className="comment-nav-badge is-comment">
                {totalReplies} total {totalReplies === 1 ? "reply" : "replies"}
              </span>
            ) : (
              <span className="comment-nav-badge is-muted">no child replies</span>
            )}
          </div>
        </div>

        <div className={`comment-book-text${isRemoved ? " is-removed" : ""}`}>
          {isRemoved ? <span className="comment-removed-placeholder">{text}</span> : text}
        </div>

        <div className="comment-book-card-foot">
          <div className="comment-nav-read-links">
            {onReply && !isRemoved && (
              <button
                type="button"
                onClick={(event) => {
                  event.stopPropagation();
                  handleReply(comment);
                }}
              >
                Reply
              </button>
            )}
            {canSoftRemove && (
              <button
                type="button"
                className="is-remove"
                aria-label={`Soft remove comment ${comment.id}`}
                onClick={(event) => {
                  event.stopPropagation();
                  handleSoftRemove(comment);
                }}
              >
                {canModerate && !isOwner ? "Remove content" : "Remove"}
              </button>
            )}
            {canHardDelete && (
              <button
                type="button"
                className="is-danger"
                aria-label={`Permanently delete comment thread ${comment.id}`}
                onClick={(event) => {
                  event.stopPropagation();
                  handleHardDeleteThread(comment);
                }}
              >
                Delete thread
              </button>
            )}
          </div>

          {hasReplies ? (
            <button
              type="button"
              className={`comment-book-toggle${isOpen ? " is-open" : ""}`}
              onClick={(event) => {
                event.stopPropagation();
                toggleReplies(comment);
              }}
            >
              {isOpen ? "Hide" : "Show"} {comment.replies.length} direct {comment.replies.length === 1 ? "reply" : "replies"}
            </button>
          ) : (
            <span className="comment-nav-badge is-muted">End of branch</span>
          )}
        </div>

        {hasReplies && isOpen && (
          <div className="comment-book-children">
            {comment.replies.map((reply) => renderCommentCard(reply, depth + 1))}
          </div>
        )}
      </article>
    );
  };

  return (
    <section className="comment-nav-shell comment-book-shell">
      <div className="comment-book-head">
        <div>
          <h3>Comment Book</h3>
          <p>
            Top-level replies are shown one at a time. Open a branch only when you want to read its child replies.
          </p>
        </div>
        <div className="comment-book-summary">
          <span>{allComments.length} comments</span>
          <span>{roots.length} top-level pages</span>
          <span>largest branch {largestBranch}</span>
        </div>
      </div>

      <div className="comment-book-carousel" aria-label="Top-level comment carousel">
        <button
          type="button"
          className="comment-book-nav-button"
          onClick={() => selectRootByIndex(selectedRootIndex - 1)}
          disabled={roots.length <= 1}
          aria-label="Previous top-level reply"
        >
          ←
        </button>

        <div className="comment-book-counter">
          <span className="comment-book-counter-label">Top-level reply</span>
          <strong>{Math.max(selectedRootIndex + 1, 1)} / {roots.length}</strong>
          <span>
            {currentRootTotalReplies > 0
              ? `${currentRootTotalReplies} nested ${currentRootTotalReplies === 1 ? "reply" : "replies"}`
              : "no nested replies"}
          </span>
        </div>

        <button
          type="button"
          className="comment-book-nav-button"
          onClick={() => selectRootByIndex(selectedRootIndex + 1)}
          disabled={roots.length <= 1}
          aria-label="Next top-level reply"
        >
          →
        </button>
      </div>

      {roots.length > 1 && (
        <div className="comment-book-dots" aria-label="Choose a top-level reply">
          {roots.map((root, index) => (
            <button
              key={root.id}
              type="button"
              className={`comment-book-dot${root.id === selectedRoot?.id ? " is-active" : ""}`}
              aria-label={`Open top-level reply ${index + 1}`}
              onClick={() => selectRootByIndex(index)}
            />
          ))}
        </div>
      )}

      <div className="comment-book-stage">
        {selectedRoot ? (
          renderCommentCard(selectedRoot, 1)
        ) : (
          <div className="comment-nav-empty">No selected comment.</div>
        )}
      </div>

      {selectedRoot && currentRootTotalReplies > 0 && (
        <div className="comment-book-footer-note">
          This page has child replies. Use the Show/Hide control inside the card to expand only this discussion branch.
          {openReplies.has(selectedRoot.id) && (
            <button type="button" onClick={collapseCurrentBranch}>Collapse this branch</button>
          )}
        </div>
      )}
    </section>
  );
};

export default CommentNavigator;
